import React, { type ReactNode, useId, useMemo } from 'react';
import type { BetResult, HistoryData } from '~/vendor/cxy/types';
import clsx from 'clsx';

import { Label } from '../Label';

import styles from './styles/PerformanceIndicator.module.scss';

/**
 * `BetResult` includes `null`, so this is a function rather than a `Record` keyed on it.
 *
 * Module scope, not per-render, and — more to the point — ONE definition. Both components below
 * carried a byte-identical copy of this switch.
 */
const indicatorClass = (betResult: BetResult): string => {
  switch (betResult) {
    case 'WON':
      return styles.success;
    case 'LOST':
      return styles.failure;
    default:
      return styles.neutral;
  }
};

type IndicatorGroupProps = {
  children: ReactNode;
  className?: string;
  count: number;
  label?: string;
  position?: 'start' | 'end';
  spacing?: 's' | 'm';
};

/**
 * The chrome both public components render: the optional label, and the group container that owns
 * the generated id, the spacing class and the direction. Extracted because the two had it
 * duplicated line for line, along with the `!history.length` guard and the comment explaining a
 * hook-ordering trap — so a fix to one would not have reached the other.
 *
 * It also retires that trap structurally rather than by convention. `useId` used to sit above each
 * component's early return purely so the hook count stayed constant across an empty-to-populated
 * transition; now it lives here, in a component that is only rendered once there is something to
 * show, and neither caller has a hook below its guard to get out of order.
 */
// No defaults for `position` or `spacing` here on purpose: the two callers disagree, and a default
// on the shared shell silently imposes one on the other. `PerformanceIndicator` defaults them;
// `TipsterPerformanceIndicator` passes them through undefined, which renders no spacing class at
// all — the behaviour it had before this shell existed. `spacing = 'm'` here gave that variant a
// `gap` it never had, and no spec caught it because every fixture sets `spacing` explicitly.
const IndicatorGroup = ({ children, className, count, label, position, spacing }: IndicatorGroupProps) => {
  const id = useId();

  return (
    <div className={clsx(styles.performanceIndicator, className)} data-cxy-name="performance-indicator">
      {label && (
        <Label className={styles.label} htmlFor={id}>
          {label}
        </Label>
      )}
      <div
        aria-label="Performance Indicator"
        className={clsx(styles.container, styles[`spacing-${spacing}`])}
        id={id}
        role="group"
        style={
          {
            '--indicator-count': count,
            justifyContent: position === 'start' ? 'flex-end' : 'flex-start',
          } as React.CSSProperties
        }
      >
        {children}
      </div>
    </div>
  );
};

export type PerformanceIndicatorProps = {
  americanFormat?: boolean;
  awayText?: string;
  className?: string;
  formatDate?: (date: string, format?: string) => string;
  hasMaxWidth?: boolean;
  history: HistoryData[];
  hitRate?: number;
  homeText?: string;
  label?: string;
  limit?: number;
  position?: 'start' | 'end';
  showHitRate?: boolean;
  showMatchInformation?: boolean;
  spacing?: 's' | 'm';
};

export const PerformanceIndicator = ({
  americanFormat = false,
  awayText = 'A',
  className,
  formatDate,
  hasMaxWidth = true,
  history,
  hitRate,
  homeText = 'H',
  label,
  limit,
  position = 'end',
  showHitRate = true,
  showMatchInformation = false,
  spacing = 'm',
}: PerformanceIndicatorProps) => {
  // Derived, not state-plus-effect. An effect never runs during server render, so the Astro apps
  // emitted the container with zero indicator children and filled them in on hydration. `useMemo`
  // also removes the hook-ordering hazard outright rather than guarding it — see the tests.
  //
  // Copy before sorting: `history.sort()` reorders the array the PARENT owns. Spread rather than
  // `toSorted`, which is ES2023 while this package targets ES2020.
  const sortedHistory = useMemo(
    () =>
      [...history].sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime()).slice(0, limit),
    [history, limit],
  );

  const defaultDateFormat = (isoString: string): string => {
    const date = new Date(isoString);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return americanFormat ? `${month}/${day}` : `${day}/${month}`;
  };

  const formatTeamName = (match: HistoryData, { showFullName }: { showFullName?: boolean } = {}): string => {
    const isAway = match.participant.side === 'AWAY';
    const team = isAway ? match.homeTeam : match.awayTeam;
    const name = showFullName ? team.name : team.shortName;

    if (americanFormat) {
      return isAway ? `@${name}` : name || team.name;
    }

    return match.participant.side === 'HOME' ? `${name} (${homeText})` : `${name} (${awayText})`;
  };

  // Guard below `useMemo` and above the render: the hook count no longer depends on `history`,
  // because the only other hook (`useId`) moved into IndicatorGroup, which is not rendered at all
  // when there is nothing to show. Pinned by the hook-ordering spec.
  if (!history.length) {
    return null;
  }

  return (
    <IndicatorGroup className={className} count={history.length} label={label} position={position} spacing={spacing}>
      {showHitRate && (
        <span className={styles.percentage} data-testid="hit-rate">
          {hitRate}%
        </span>
      )}
      {sortedHistory.map((match, index) => (
        <div
          className={clsx(styles.indicatorWrapper, {
            [styles.hasMaxWidth]: hasMaxWidth,
          })}
          data-testid="indicator"
          key={index}
        >
          {showMatchInformation && (
            <span className={styles.dateTop} data-testid="date-top">
              {formatDate ? formatDate(match.startTime, 'M/d') : defaultDateFormat(match.startTime)}
            </span>
          )}
          <div
            aria-label={`${match.participant.betResult === 'WON' ? 'Success' : match.participant.betResult === 'LOST' ? 'Failure' : 'Undetermined'} against ${formatTeamName(match)}`}
            className={clsx(styles.indicator, indicatorClass(match.participant.betResult))}
            role="img"
          />
          {showMatchInformation && (
            <>
              <span className={styles.teamName}>{formatTeamName(match)}</span>
              <span className={styles.dateBottom} data-testid="date-bottom">
                {formatDate ? formatDate(match.startTime, 'M/d') : defaultDateFormat(match.startTime)}
              </span>
            </>
          )}
        </div>
      ))}
    </IndicatorGroup>
  );
};

export type TipsterPerformanceIndicatorProps = Pick<
  PerformanceIndicatorProps,
  'className' | 'hasMaxWidth' | 'position' | 'spacing' | 'label'
> & { history: BetResult[] };

export const TipsterPerformanceIndicator = ({
  className,
  hasMaxWidth,
  history,
  label,
  position,
  spacing,
}: TipsterPerformanceIndicatorProps) => {
  // No hooks here at all now, so the guard can sit first — the shell owns `useId`.
  if (!history.length) {
    return null;
  }

  return (
    <IndicatorGroup className={className} count={history.length} label={label} position={position} spacing={spacing}>
      {history.map((result, index) => (
        <div
          className={clsx(styles.indicatorWrapper, {
            [styles.hasMaxWidth]: hasMaxWidth,
          })}
          data-testid="indicator"
          key={index}
        >
          <div className={clsx(styles.indicator, indicatorClass(result))} role="img" />
        </div>
      ))}
    </IndicatorGroup>
  );
};
